import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { resolveCellWidth, resolveColumns } from "./grid.utils";
import type { IGridMetrics } from "./types";

const EMPTY_METRICS: IGridMetrics = { width: 0, columns: 0, cellWidth: 0 };

/**
 * Measure the canvas so both placement and resize work off real geometry.
 *
 * Measured on the element, not the viewport: the sidebar collapse and the
 * split-screen panel both change how wide the dashboard actually is, and a
 * viewport media query is blind to either. `columns` stays 0 until the first
 * measurement lands so callers can hold off rendering rather than flash a
 * one-column layout.
 */
export function useGridMetrics<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [metrics, setMetrics] = useState<IGridMetrics>(EMPTY_METRICS);

  const measure = useCallback((element: HTMLElement) => {
    const width = element.clientWidth;
    const columns = resolveColumns(width);
    const cellWidth = resolveCellWidth(width, columns);

    setMetrics((previous) =>
      previous.width === width &&
      previous.columns === columns &&
      previous.cellWidth === cellWidth
        ? previous
        : { width, columns, cellWidth },
    );
  }, []);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    measure(element);

    const observer = new ResizeObserver(() => measure(element));
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  return { ref, metrics, isMeasured: metrics.columns > 0 };
}
